import { groupBy } from "../utils/utils";
import postsRaw from "./posts-raw.json";
import { Post, PostImage } from "./types";

export const tagsMap = Object.fromEntries(
  Object.entries(
    groupBy(
      postsRaw.flatMap((p) => p.tags),
      (t) => t,
    ),
  ).map(([t, ts]) => [t, { name: t, count: ts.length }]),
);

export const tags = Object.entries(tagsMap)
  .map(([, t]) => t)
  .sort((a, b) => a.name.localeCompare(b.name))
  .sort((a, b) => b.count - a.count);

export const posts: Post[] = postsRaw.map((p) => {
  const mkPath = (
    filename: string,
    version: keyof PostImage["urls"] = "full",
  ) => {
    const prefix = `https://files.irisarco.art/${p.createdAt.slice(0, 4)}/`;

    if (version === "full") {
      return prefix + filename;
    } else if (
      ["medium", "share"].includes(version) &&
      (filename.endsWith(".mp4") || filename.endsWith(".gif"))
    ) {
      return prefix + filename;
    } else {
      const n = filename.split(".");
      const ext = n.pop();
      const name = n.join(".");
      const finalExt = (() => {
        if (ext === "png") {
          return version === "share" ? "jpg" : "webp";
        }
        if (ext === "gif") {
          return "webp";
        }
        return ext;
      })();

      return `${prefix}${name}_${version}.${finalExt}`;
    }
  };

  return {
    ...p,
    altvStatus: p.altvStatus as Post["altvStatus"],
    altv: p.altv && {
      ...p.altv,
      url: mkPath(p.altv.filename),
    },
    images: p.images.map((i) => ({
      ...i,
      urls: {
        full: mkPath(i.filename),
        medium: mkPath(i.filename, "medium"),
        thumb: mkPath(i.filename, "thumb"),
        thumbSquare: mkPath(i.filename, "thumbSquare"),
        share: mkPath(i.filename, "share"),
      },
    })),
    tags: p.tags.map((t) => tagsMap[t]),
  };
});

export const groupedPosts = Object.entries(
  groupBy(posts, (p) => p.createdAt.slice(0, 4)),
).sort(([a], [b]) => Number(b) - Number(a));

const shuffle = <T>(array: T[], seed: number): T[] => {
  array = [...array];
  let m = array.length,
    t,
    i;

  // While there remain elements to shuffle…
  while (m) {
    // Pick a remaining element…
    i = Math.floor(random(seed) * m--);

    // And swap it with the current element.
    t = array[m];
    array[m] = array[i];
    array[i] = t;
    ++seed;
  }

  return array;
};

const random = (seed: number) => {
  const x = Math.sin(seed++) * 10000;
  return x - Math.floor(x);
};

export const getSimilarPosts = (post: Post): Post[] =>
  shuffle(posts, post.id)
    .map((p) => ({
      post: p,
      score:
        p.id === post.id
          ? 0
          : post.tags
              .filter((t) => !["futa", "altv"].includes(t.name))
              .map((t) => p.tags.find((pt) => pt.name === t.name)?.count || 0)
              .reduce((sum, n) => sum + Number(n ? 1 / n : 0), 0),
    }))
    .sort((a, b) => b.score - a.score)
    .filter((p) => p.score > 0)
    .slice(0, 2)
    .map(({ post }) => post);
